import React, { useState, useEffect, useMemo, useRef } from 'react';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import GridItem from 'components/Grid/GridItem';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import { DisplayS, HeadingSBold, BodyS, BodyMBold, BodySBold, BodyM } from 'components/Typography/Typography';
import { translate } from 'components/Translation';
import GridContainer from 'components/Grid/GridContainer';
import styles from 'assets/jss/material-dashboard-pro-react/views/numberDetails/detailsStyle';
import { makeStyles } from '@material-ui/core/styles';
import { catchError, concatMap, finalize } from 'rxjs/operators';
import { EMPTY, throwError } from 'rxjs';
import { ECommerceService } from 'services/eCommerce/eCommerce';
import { useDispatch, useSelector } from 'react-redux';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import Button from 'components/CustomButtons/Button';
import DOMPurify from 'dompurify';
import VatClause from 'components/VatClause/VatClause';
import { SingleCustomerProduct } from 'utils/api/models/products/singleCustomerProduct';
import moment from 'moment';
import rocketImg from 'assets/img/rocket.png';
import { CommitmentDetails } from 'utils/api/models/payments/commitmentDetails';
import { getBwodLabels } from 'redux/modules/dynamicLabels/selectors';
import { SingleCommitmentDetails } from 'utils/api/models/payments/singleCommitmentDetails';

// @ts-ignore
const useStyles = makeStyles(styles);

export interface ITariffPlanCardProps {
	isPostpaid: boolean;
	changePlanAvailable?: boolean;
	tariffPlan: SingleCustomerProduct;
	tryAndBuyProduct?: SingleCustomerProduct;
	corpFbbCommitment: SingleCustomerProduct | undefined;
	commitmentDetails: CommitmentDetails[];
	tryAndBuyDuration?: number;
	tryAndBuyPlanName?: string;
	onClick: () => void;
	phoneNumber: string;
	accountType: string;
	billingType: string;
	isAamaliPlus?: boolean;
	showHeading?: boolean;
	isFbb?: boolean;
	isWfbb?: boolean;
	showBwodCard?: boolean;
	activeCommitMents?: SingleCommitmentDetails;
	tarrifPlanBanner?: { heading?: string; body: string };
	setTarrifPlanIds: Function;
	isHighestPlanActive?: boolean;
	tariffPlanPending: boolean;
}

const TariffPlanCard = ({
	isPostpaid,
	tariffPlan,
	onClick,
	changePlanAvailable,
	tryAndBuyProduct,
	tryAndBuyDuration,
	tryAndBuyPlanName,
	phoneNumber,
	billingType,
	accountType,
	isAamaliPlus,
	showHeading,
	showBwodCard = false,
	isFbb = false,
	isWfbb = false,
	corpFbbCommitment,
	commitmentDetails,
	activeCommitMents,
	tarrifPlanBanner,
	setTarrifPlanIds,
	isHighestPlanActive,
	tariffPlanPending,
}: ITariffPlanCardProps) => {
	const [tariffPlanDescription, setTariffPlanDescription] = useState<string>('');

	const [loadingDescription, setLoadingDescription] = useState<boolean>(false);
	const classes = useStyles();
	const showTariffPlanDescription = isPostpaid && !!tariffPlanDescription;
	const dispatch = useDispatch();
	const bwodLabels = useSelector(getBwodLabels);
	const isMounted = useRef(true);

	const tryAndBuyEndDate = useMemo(() => {
		if (tryAndBuyProduct) {
			return moment(tryAndBuyProduct.startDate)
				.add(tryAndBuyDuration, 'months')
				.format('DD.MM.YYYY');
		}
		return '';
	}, [tryAndBuyDuration, tryAndBuyProduct]);
	const computedTariffPlan: string = useMemo(() => {
		if (isFbb) {
			return tryAndBuyPlanName || activeCommitMents?.name || translate('undefined');
		} else if (isPostpaid) {
			return tryAndBuyPlanName || tariffPlan?.name || translate('undefined');
		}
		return translate('hayyak');
	}, [isPostpaid, tariffPlan, tryAndBuyPlanName, activeCommitMents, isFbb]);

	useEffect(() => {
		setLoadingDescription(true);
		const id = tryAndBuyProduct?.productId || tariffPlan?.productId;
		const getContentByIdResponse = ECommerceService.getContentByPartNumber(id).pipe(
			concatMap(({ response }) => {
				if (response.catalogEntryView && response.catalogEntryView.length === 1) {
					const uniqueId = response.catalogEntryView[0].uniqueID;
					return ECommerceService.getContentById(uniqueId);
				}
				return EMPTY;
			}),
			catchError((error) => {
				if (isMounted.current) {
					setLoadingDescription(false);
				}
				return throwError(error);
			})
		);
		getContentByIdResponse
			.pipe(
				finalize(() => {
					if (isMounted.current) {
						setLoadingDescription(false);
					}
				})
			)
			.subscribe(
				({ response }) => {
					if (response.catalogEntryView && response.catalogEntryView.length === 1) {
						if (isMounted.current) {
							setTariffPlanDescription(response.catalogEntryView[0].longDescription);
						}
					}
				},
				(error) => throwError(error)
			);
	}, [tryAndBuyProduct, tariffPlan, dispatch]);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);
	return (
		<ConditionalRender
			show={!loadingDescription}
			onTrue={() => (
				<Card className={isFbb && showBwodCard ? classes.bannerClass : classes.bundleCard}>
					<ConditionalRender
						show={isFbb && !!tarrifPlanBanner?.body}
						onTrue={() => (
							<div className={classes.bannerWrapper}>
								<div>
									<BodyMBold customClassName={classes.bannerText}>
										{tarrifPlanBanner?.heading}
									</BodyMBold>
									<BodyM customClassName={classes.bannerText}>{tarrifPlanBanner?.body}</BodyM>
								</div>

								<img className={classes.rocketImg} src={rocketImg} alt="rocketImg" />
							</div>
						)}
					/>

					<CardBody className={classes.tarrifCardBody}>
						<HeadingSBold color={tryAndBuyProduct ? 'enterprise' : 'disabled-light'}>
							{tryAndBuyProduct ? translate('numbers.details.new.tariff') : translate('current.plan')}
						</HeadingSBold>
						<div className={classes.activeButtonWrapper}>
							<DisplayS color="secondary-light">{computedTariffPlan}</DisplayS>
							<ConditionalRender
								show={!tariffPlanPending && isFbb && activeCommitMents?.status === 'Active'}
								onTrue={() => (
									<Button color="primary-light" className={classes.activeLabel}>
										{translate('badge.active')}
									</Button>
								)}
							/>
						</div>
						<ConditionalRender
							show={!!activeCommitMents && !tryAndBuyEndDate && activeCommitMents?.status === 'Active'}
							onTrue={() => (
								<div className={classes.dateWrapper}>
									<BodySBold color="secondary-light">
										{translate('numbers.details.base.tariff.will.end')}
									</BodySBold>
									<BodySBold customClassName={classes.planExpiryDate}>
										{moment(activeCommitMents?.endDate).format('Do MMMM YYYY')}
									</BodySBold>
								</div>
							)}
						/>
						<ConditionalRender
							show={!!tryAndBuyEndDate}
							onTrue={() => (
								<BodyMBold color="disabled-light">
									{translate('numbers.details.new.tariff.will.end', tryAndBuyEndDate)}
								</BodyMBold>
							)}
						/>
						<ConditionalRender
							show={showTariffPlanDescription && !tryAndBuyProduct}
							onTrue={() => (
								<GridContainer className={classes.bundleCardDetails} justify="flex-start">
									<GridItem xs={12}>
										<div
											dangerouslySetInnerHTML={{
												__html: DOMPurify.sanitize(tariffPlanDescription, {
													ALLOWED_TAGS: ['span', 'br'],
												}),
											}}
										/>
									</GridItem>
								</GridContainer>
							)}
						/>
						<ConditionalRender show={!tryAndBuyProduct && !isFbb} onTrue={() => <VatClause />} />
						<ConditionalRender
							show={!!tryAndBuyProduct}
							onTrue={() => (
								<div className={classes.tryAndBuyDescription}>
									<BodySBold disableMargin inline>
										{translate('number.details.tariff.plan.card.try.and.buy.description1')}
									</BodySBold>
									<BodyS disableMargin inline>
										{translate(
											'number.details.tariff.plan.card.try.and.buy.description2',
											tariffPlan?.name
										)}
									</BodyS>
									<div className={classes.tryAndBuyUpgradePlan}>
										<BodySBold disableMargin>
											<span
												dangerouslySetInnerHTML={{
													__html: translate('number.details.tariff.plan.card.upgrade.plan'),
												}}
											></span>
										</BodySBold>
									</div>
								</div>
							)}
						/>
						<ConditionalRender
							show={!!changePlanAvailable}
							onTrue={() => (
								<GridContainer justify={'flex-end'}>
									<ConditionalRender
										show={tariffPlanPending}
										onTrue={() => (
											<GridItem xs={12} className={classes.changePlanPending}>
												<BodySBold color={'brand-secondary'} disableMargin>
													{translate('numbers.details.change-plan.pending')}
												</BodySBold>
											</GridItem>
										)}
									/>
									<GridItem>
										{!isAamaliPlus && !isWfbb ? (
											<Button
												color="primary-light"
												onClick={onClick}
												disabled={tariffPlanPending || isHighestPlanActive}
											>
												{translate('numbers.details.upgrade-plan')}
											</Button>
										) : (
											<></>
										)}
									</GridItem>
								</GridContainer>
							)}
						/>
					</CardBody>
				</Card>
			)}
		/>
	);
};

export default TariffPlanCard;
